import type { Channel } from 'types/masterData'

export enum ContentTypeResponse {
	AbTest = 'a_b_test',
	HeroBanner = 'hero_banner',
	HeroBannerSecondaryLines = 'hero_banner_secondary_lines',
	FamiliesBanner = 'families_banner',
	FamilyBannerShop = 'family_banner_shop',
	FamiliesCarousel = 'families_carousel',
	LineBanner = 'line_banner',
	LinesBanner = 'lines_banner',
	FamilyBanner = 'family_banner',
	FamiliesDirectAccess = 'families_direct_access',
	FamilyDirectAccess = 'family_direct_access',
	CommBanner = 'comm_banner',
	PromotionContent = 'promotion_content',
	CommsBannerCarousel = 'comms_banner_carousel',
	LinesDirectAccess = 'lines_direct_access',
	CrossSellingCarousel = 'cross_selling_carousel',
	HeroBannerShop = 'hero_banner_shop',
	HeroBannerShopMultibrand = 'hero_banner_shop_multibrand',
	HeroBannerShopSales = 'hero_banner_sales_shop',
	PromotionalBanner = 'promotional_banner',
	CarouselBanner = 'carousel_banner',
	SeoBanner = 'seo_banner',
	CapsuleBanner = 'capsule_banner',
	LegalTextBanner = 'legal_text_banner',
}

export enum ImageContentTypes {
	jpeg = 'image/jpeg',
	svg = 'image/svg+xml',
}

export interface FileNode {
	url: string
	contentType: string
}

export interface ImageNode {
	url: string
	contentType: ImageContentTypes
	description: string
	dimension: {
		width: number
		height: number
	}
}

export interface ImageFile {
	edges: {
		node: ImageNode
	}[]
}

export interface File {
	edges: {
		node: FileNode
	}[]
}

export interface PointOfInterestImageField {
	horizontal: number
	vertical: number
	file: ImageFile
}

export enum HeroBannerSectionResponseType {
	ctas = 'ctas',
	primaryLines = 'primary_lines',
	promo = 'promo',
}

interface Cta {
	href: string
	title: string
	ctaAnalyticsId: string
	inverse: boolean
	legacy: boolean
	noFollow: boolean
	target: string
}

interface SimpleCta {
	labelKey: string
}

interface SectionSimpleCta {
	labelKey: string
	sectionId: string
}

interface SystemCommonFieldsResponse {
	contentType: ContentTypeResponse
	uid: string
	tags: string[]
}

export type CtaResponse = {
	labelKey: string
	sectionId: string | null
	brandId: string | null
	featureId: string | null
}

export type CtasResponse = CtaResponse[]

export type SectionCtasResponse = {
	labelKey: string
	sectionId: string | null
	brandIdSelector: {
		brandId: string
	}
	featureIdSelector?: {
		featureId: FEATURE_ID | null
	}
}[]

export interface ContentAbTestUidResponse {
	system: SystemCommonFieldsResponse & {
		contentType: ContentTypeResponse.AbTest
	}
}

export interface ContentAbTestResponse {
	testKey: string
	variants: {
		edges: SectionsNodeResponse[]
	}
	system: SystemCommonFieldsResponse & {
		contentType: ContentTypeResponse.AbTest
	}
}

export interface ContentHeroBannerSecondaryLinesResponse {
	title: string
	subTitle: string
	simpleCta: SimpleCta
	catalogId: string
	promotionName: string
	image: PointOfInterestImageField
	ctaList: {
		ctas: CtasResponse | null
	} | null
	system: SystemCommonFieldsResponse & {
		contentType:
			| ContentTypeResponse.HeroBannerSecondaryLines
			| ContentTypeResponse.HeroBanner
	}
}

export enum ContentHeroBannerPromoType {
	general = 'general',
	progressive = 'progressive',
	family = 'family',
}

export enum ContentHeroBannerAvailableColors {
	red = 'red',
	black = 'black',
	lightBlue = 'light-blue',
	darkBlue = 'dark-blue',
	lightGreen = 'light-green',
	darkGreen = 'dark-green',
	orange = 'orange',
	lightBurgundy = 'light-burgundy',
	darkBurgundy = 'dark-burgundy',
}

interface PromoTitles {
	title: string
	discount: string
}

interface PromoSubtitles {
	condition: string
	onlineExclusive: boolean
	dueDate: string
	promoCode: string
}

interface ProgressivePromoTitles {
	title1: string
	strategy1: string
	title2: string
	strategy2: string
}

interface FamilyPromoTitles {
	title: string
	subtitle: string
}

interface ContentHeroBannerPromo {
	promoType: ContentHeroBannerPromoType
	countdown: string
	promoBackgroundColor: ContentHeroBannerAvailableColors
	promoTitles: PromoTitles
	progressivePromoTitles: ProgressivePromoTitles
	familyPromoTitles: FamilyPromoTitles
	promoSubtitles: PromoSubtitles
	sectionId: string
	promoLegalInfo: string
}

export interface ContentHeroBannerResponse {
	type: HeroBannerSectionResponseType
	promotionName: string
	image: PointOfInterestImageField
	linesCtas: boolean
	ctas: Cta[]
	ctaList: {
		ctas: CtasResponse | null
	} | null
	title: string
	subTitle: string
	simpleCta: SimpleCta
	catalogId: string
	promo: ContentHeroBannerPromo
	system: SystemCommonFieldsResponse & {
		contentType: ContentTypeResponse.HeroBanner
	}
}

export interface ContentLineBannerResponse {
	promotionName: string
	text: string
	sectionId: string
	image: PointOfInterestImageField
	system: SystemCommonFieldsResponse & {
		contentType: ContentTypeResponse.LineBanner
	}
}

export interface ContentLinesBannerResponse {
	lineBanners: {
		edges: {
			node: ContentLineBannerResponse
		}[]
	}
	system: SystemCommonFieldsResponse & {
		contentType: ContentTypeResponse.LinesBanner
	}
}

export interface ContentFamilyBannerResponse {
	promotionName: string
	text: string
	sectionId: string
	image: PointOfInterestImageField
	system: SystemCommonFieldsResponse & {
		contentType: ContentTypeResponse.FamilyBanner
	}
}

export interface ContentFamilyBannerShopResponse {
	contentColor: TextColor
	promotionName: string
	familySections: {
		titleKey: string
		cta: SectionSimpleCta
		image: PointOfInterestImageField
	}[]
	system: SystemCommonFieldsResponse & {
		contentType: ContentTypeResponse.FamilyBannerShop
	}
}

export interface ContentFamiliesBannerResponse {
	families: {
		edges: {
			node: ContentFamilyBannerResponse
		}[]
	}
	system: SystemCommonFieldsResponse & {
		contentType: ContentTypeResponse.FamiliesBanner
	}
}

export interface ContentFamiliesCarouselResponse {
	title: string
	showLink: boolean
	sectionId: string
	families: {
		edges: {
			node: ContentFamilyBannerResponse
		}[]
	}
	system: SystemCommonFieldsResponse & {
		contentType: ContentTypeResponse.FamiliesCarousel
	}
}

export interface ContentFamilyDirectAccessResponse {
	promotionName: string
	textLabel: string
	sectionId: string
	image: PointOfInterestImageField
	system: SystemCommonFieldsResponse & {
		contentType: ContentTypeResponse.FamilyDirectAccess
	}
}

export interface ContentFamiliesDirectAccessResponse {
	familiesDirectAccess: {
		edges: {
			node: ContentFamilyDirectAccessResponse
		}[]
	}
	system: SystemCommonFieldsResponse & {
		contentType: ContentTypeResponse.FamiliesDirectAccess
	}
}

export enum COMM_BANNER_TYPE {
	PROMO = 'promo',
	INFO = 'info',
}

export enum COMM_BANNER_BG_COLOR_TYPE {
	WHITE = 'white',
	BLACK = 'black',
	RED = 'red',
	LIGHT_BLUE = 'lightBlue',
	DARK_BLUE = 'darkBlue',
	LIGHT_GREEN = 'lightGreen',
	DARK_GREEN = 'darkGreen',
	ORANGE = 'orange',
	LIGHT_BURGUNDY = 'lightBurgundy',
	DARK_BURGUNDY = 'darkBurgundy',
	CUSTOM = 'custom',
}

export enum PROMOTIONAL_BANNER_CONTENT_COLOR_TYPE {
	DEFAULT = 'default',
	PROMO = 'promo',
	CUSTOM = 'custom',
}

export enum TextColor {
	White = 'white',
	Black = 'black',
}

export enum FEATURE_ID {
	MangoLikesYou = 'mango-likes-you',
	Discover = 'discover',
	Help = 'help',
}

export interface PromotionContentResponse {
	promotionTitle: string
	discount: string
	subtitle: string
	subtitlesGroup: {
		titleKey: string
		subtitleKey: string
	}[]
	lineCtas: boolean
	sectionCtas: SectionCtasResponse
	infoText: string
	startDate: string
	endDate: string
	promotionName: string
	system: SystemCommonFieldsResponse & {
		contentType: ContentTypeResponse.PromotionContent
	}
}

export interface ContentCommBannerResponse {
	type: COMM_BANNER_TYPE
	promotion: {
		edges: {
			node: PromotionContentResponse
		}[]
	}
	infoTitle: string
	infoSubtitle: string
	infoLineCtas: boolean
	infoPromotionName: string
	infoSectionCtas: SectionCtasResponse
	closable: boolean
	contentColor: TextColor
	backgroundColorType: string
	customBackgroundColor: string
	showInfo: boolean
	showCountdown: boolean
	timeBeforeEndToShowCountdown: number
	system: SystemCommonFieldsResponse & {
		contentType: ContentTypeResponse.CommBanner
	}
}

export interface ContentCommsCarouselResponse {
	comms: {
		edges: {
			node: ContentCommBannerResponse
		}[]
	}
	system: SystemCommonFieldsResponse & {
		contentType: ContentTypeResponse.CommsBannerCarousel
	}
}

export interface ContentLinesDirectAccessResponse {
	promotionName: string
	system: SystemCommonFieldsResponse & {
		contentType: ContentTypeResponse.LinesDirectAccess
	}
}

export enum HeroBannerShopType {
	image = 'image',
	video = 'video',
}

export enum PROMOTIONAL_BANNER_TYPE {
	High = 'high',
	Low = 'low',
}

export interface HeroBannerShopVideo {
	videoPortrait: File
	videoLandscape: File
	videoPlaceholder: ImageFile
}

export enum HERO_BANNER_SHOP_PLUS_TITLE_TYPE {
	INSPIRATIONAL = 'inspirational',
	MANGO_SANS = 'mangoSans',
	IMAGE = 'image',
}

export interface ContentHeroBannerShopResponse {
	titleType: HERO_BANNER_SHOP_PLUS_TITLE_TYPE
	titleKey: string | null
	titleKeyLanguage: string | null
	titleImage: {
		svgImage: ImageFile
		mobileWidth: number
		desktopWidth: number
	} | null
	extraMobileTitleImage: boolean | null
	mobileTitleImage: {
		svgImage: ImageFile
		mobileWidth: number
	} | null
	type: HeroBannerShopType
	promotionName: string
	contentColor: TextColor
	image: PointOfInterestImageField
	extraImage: boolean
	alternativeImage: PointOfInterestImageField | null
	video: HeroBannerShopVideo
	ctas: SectionCtasResponse
	system: SystemCommonFieldsResponse & {
		contentType: ContentTypeResponse.HeroBannerShop
	}
}

export interface ContentHeroBannerShopMultibrandResponse {
	titleKey: string
	contentColor: TextColor
	promotionName: string
	defaultImage: PointOfInterestImageField
	extraImage: boolean
	alternativeImage: PointOfInterestImageField | null
	brandCtas: {
		brandId: string
		image: PointOfInterestImageField
	}[]
	sectionCtas: {
		labelKey: string
		sectionId: string
		image: PointOfInterestImageField
		featureIdSelector?: {
			featureId: FEATURE_ID | null
		}
	}[]
	system: SystemCommonFieldsResponse & {
		contentType: ContentTypeResponse.HeroBannerShopMultibrand
	}
}

export interface ContentHeroBannerShopSalesResponse {
	promotion: {
		edges: {
			node: PromotionContentResponse
		}[]
	}
	subtitleTopKey: string
	subtitleBottomGroup: {
		titleKey: string
		subtitleKey: string
	}[]
	contentColor: TextColor
	showInfo: boolean
	type: HeroBannerShopType
	image: PointOfInterestImageField
	extraImage: boolean
	alternativeImage: PointOfInterestImageField | null
	video: HeroBannerShopVideo
	system: SystemCommonFieldsResponse & {
		contentType: ContentTypeResponse.HeroBannerShopSales
	}
}

export interface ContentPromotionalBannerResponse {
	type: PROMOTIONAL_BANNER_TYPE
	contentColorType: PROMOTIONAL_BANNER_CONTENT_COLOR_TYPE | null
	customContentColor: string | null
	infoIconColor: TextColor
	showDefaultDiscount: boolean
	defaultDiscount: string | null
	subtitleTopKey: string
	promotion: {
		edges: {
			node: PromotionContentResponse
		}[]
	}
	image: PointOfInterestImageField
	system: SystemCommonFieldsResponse & {
		contentType: ContentTypeResponse.PromotionalBanner
	}
}

export enum CarouselBannerType {
	Products = 'products',
	BestSellers = 'best_sellers',
	SectionId = 'section_id',
}

interface LinkLabel {
	labelId: string
}

export interface SeoLink extends LinkLabel {
	url: string
	priority?: boolean
}

export interface SubbrandLinks extends LinkLabel {
	links: SeoLink[]
}

export interface SeoBannerDataResponse {
	id: string
	items: SubbrandLinks[]
}

export interface SeoBannerFailedResponse {
	origin: string
	message: string
}

export interface ContentSeoBannerResponse {
	seoBannerLinks: SeoBannerDataResponse[] | SeoBannerFailedResponse
	system: SystemCommonFieldsResponse & {
		contentType: ContentTypeResponse.SeoBanner
	}
}

export interface ContentCarouselBannerResponse {
	products?: CrossSellingServiceResponse[]
	titleKey: string
	imageType: string
	type: CarouselBannerType
	showCta: boolean
	cta: SectionSimpleCta
	promotionName: string
	system: SystemCommonFieldsResponse & {
		contentType: ContentTypeResponse.CarouselBanner
	}
}

export interface ContentLegalTextBannerResponse {
	legalText: string
	system: SystemCommonFieldsResponse & {
		contentType: ContentTypeResponse.CarouselBanner
	}
}

export type SectionsResponse =
	| ContentAbTestUidResponse
	| ContentFamilyDirectAccessResponse
	| ContentHeroBannerResponse
	| ContentHeroBannerSecondaryLinesResponse
	| ContentFamiliesBannerResponse
	| ContentFamiliesCarouselResponse
	| ContentFamiliesDirectAccessResponse
	| ContentCommsCarouselResponse
	| ContentLinesBannerResponse
	| ContentLinesDirectAccessResponse
	| ContentCrossSellingCarouselResponse
	| ContentHeroBannerShopResponse
	| ContentHeroBannerShopMultibrandResponse
	| ContentFamilyBannerShopResponse
	| ContentHeroBannerShopSalesResponse
	| ContentPromotionalBannerResponse
	| ContentCarouselBannerResponse
	| ContentCapsuleBannerResponse
	| ContentSeoBannerResponse
	| ContentLegalTextBannerResponse

export type SectionsNodeResponse = {
	node: SectionsResponse
}

export interface ContentHomeResponseItem {
	priority?: number | null
	sections: {
		edges: SectionsNodeResponse[]
	}
	seo: {
		description: string
		title: string
	}
}

export interface ContentHomeResponseItems {
	items: ContentHomeResponseItem[]
}

export interface ContentAllHomesResponse<T extends Channel> {
	all_home_outlet?: T extends Channel.Outlet
		? ContentHomeResponseItems
		: undefined
	all_home_shop?: T extends Channel.Shop ? ContentHomeResponseItems : undefined
}

export interface ContentSingleHomeResponse<T extends Channel> {
	home?: T extends Channel.Outlet ? ContentHomeResponseItem : undefined
	home_shop?: T extends Channel.Shop ? ContentHomeResponseItem : undefined
}
export type ContentHomeResponse<T extends Channel> =
	| ContentAllHomesResponse<T>
	| ContentSingleHomeResponse<T>
	| null

export interface CrossSellingServiceResponse {
	productId: string
	colorId: string
	lookId: string
	imageType?: string
}

export interface ContentCrossSellingCarouselResponse {
	crossSellingType: string
	imageType: string
	promotionName: string
	bestSellersBrand: string
	catalog: {
		catalogId: string
		title: string
	}
	customProducts: {
		title: string
		products: CrossSellingServiceResponse[]
	}
	system: SystemCommonFieldsResponse & {
		contentType: ContentTypeResponse.CrossSellingCarousel
	}
}

export enum CapsuleBannerType {
	Moments = 'moments',
	Collections = 'collections',
}

export interface ContentCapsuleBannerResponse {
	type: CapsuleBannerType
	contentColor: TextColor
	image: PointOfInterestImageField
	titleKey: string
	tag: string
	ctaList: {
		ctas: CtasResponse | null
	} | null
	sectionId: string
	promotionName: string
	system: SystemCommonFieldsResponse & {
		contentType: ContentTypeResponse.CapsuleBanner
	}
}
